import type { MediaAsset } from '-/Custom/Util.types';
import { Image } from 'expo-image';
import { useLocalSearchParams } from 'expo-router';
import { useRef } from 'react';
import { ScrollView, View } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import Button from '@/components/UI/Button';
import Icon from '@/components/Unistyles/Icon';
import useScrollToEnd from '@/hooks/useScrollToEnd';
import useValueWithLast from '@/hooks/useValueWIthLast';
import { removeChatMedia, useChatStore } from '@/stores/useChatStore';

const UnistylesImage = withUnistyles(Image);

function ChatMediaInputBar() {
  const { chatId: id = '' } = useLocalSearchParams<{ chatId: string }>();
  const scrollViewRef = useRef<ScrollView>(null);
  const chatMedia = useChatStore((state) => state.chatMedia.get(id));
  const [mediaCount, lastMediaCount] = useValueWithLast(chatMedia?.size);

  const onPressRemoveButton = (asset: MediaAsset) => {
    removeChatMedia(id, asset);
  };

  useScrollToEnd(
    scrollViewRef,
    (mediaCount || 0) > (lastMediaCount || 0),
    mediaCount,
  );

  return chatMedia?.size ? (
    <ScrollView
      ref={scrollViewRef}
      style={style.container}
      contentContainerStyle={style.content}
      horizontal
    >
      {Array.from(chatMedia || []).map((asset) => (
        <View key={asset.id} style={style.item}>
          <UnistylesImage
            transition={500}
            style={style.image}
            source={asset.path}
          />
          <Button
            iconOnly
            variant="secondary"
            style={style.removeButton}
            accessibilityLabel="Remove attachment"
            onPress={() => {
              void onPressRemoveButton(asset);
            }}
          >
            <Icon variant="white" name="close-outline" size="xl" />
          </Button>
        </View>
      ))}
    </ScrollView>
  ) : null;
}

const style = StyleSheet.create((theme) => ({
  container: {
    flexGrow: 1,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors.borderMuted,
  },
  content: {
    padding: theme.space('md'),
    gap: theme.space('md'),
  },
  item: {
    width: 100,
    height: 100,
    borderRadius: 16,
    overflow: 'hidden',
  },
  image: {
    width: 100,
    height: 100,
    borderRadius: 16,
  },
  removeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderBottomLeftRadius: theme.borderRadius.xl,
    padding: theme.space('sm'),
  },
}));

export default ChatMediaInputBar;
